import {
  deleteElement,
  fetchElements,
  saveElements,
  saveElementsType,
  updateElement,
} from "@/lib/utils";
import type {
  ActuacionesAU,
  Contribuccion,
  FileItem,
  IndicadoresAU,
  ProjectAU,
  ValoresAU,
} from "@/types/types";
import type { ProjectsAURepository } from "@/lib/repositories/interfaces/projects-au-repository.interface";

const PROYECTOS_AU_FIELDS = [
  "*",
  "translations.*",
  "feder_actuaciones.*",
  "feder_actuaciones.translations.*",
  "feder_actuaciones.feder_proyecto.*",
  "feder_actuaciones.feder_proyecto.translations.*",
  "feder_actuaciones.feder_proyecto.fondo_feder.*",
  "actuaciones_propias.*",
  "actuaciones_propias.translations.*",
  "actuaciones_propias.proyecto_propio.*",
  "actuaciones_propias.proyecto_propio.translations.*",
  "folder.*",
  "folder.files.*",
] as const;

const ACTUACIONES_FIELDS = ["*", "translations.*"] as const;

const INDICADORES_FIELDS = ["*", "translations.*", "valores_aue.*"] as const;

export const directusProjectsAURepository: ProjectsAURepository = {
  // Proyectos
  fetchProjectsByTenant: (tenantId) =>
    fetchElements<ProjectAU>(
      "proyectos_au",
      [...PROYECTOS_AU_FIELDS],
      undefined,
      { tenant: { _eq: tenantId } }
    ),
  async createProject(payload) {
    const [created] = await saveElementsType<ProjectAU>("proyectos_au", [
      payload,
    ]);
    return created;
  },
  updateProject: (id, payload) => updateElement("proyectos_au", id, payload),
  deleteProject: (id) => deleteElement("proyectos_au", id),
  // Contribuciones
  fetchContribucionesByProject: (projectId) =>
    fetchElements<Contribuccion>(
      "contribucion",
      ["*"],
      undefined,
      { proyecto_au: { _eq: projectId } }
    ),
  fetchContribucionesByProjects: (projectIds) =>
    projectIds.length
      ? fetchElements<Contribuccion>(
          "contribucion",
          ["*"],
          undefined,
          { proyecto_au: { _in: projectIds } }
        )
      : Promise.resolve([]),
  createContribuciones: (payloads) =>
    payloads.length
      ? saveElements<Contribuccion>("contribucion", payloads)
      : Promise.resolve([]),
  updateContribucion: (id, payload) =>
    updateElement("contribucion", id, payload),
  deleteContribucion: (id) => deleteElement("contribucion", id),
  // Actuaciones
  fetchActuacionesByTenant: (tenantId) =>
    fetchElements<ActuacionesAU>(
      "actuaciones",
      [...ACTUACIONES_FIELDS],
      undefined,
      {
        proyecto: {
          tenant: { _eq: tenantId },
        },
      }
    ),
  async createActuacion(payload) {
    const [created] = await saveElementsType<ActuacionesAU>("actuaciones", [
      payload,
    ]);
    return created;
  },
  updateActuacion: (id, payload) => updateElement("actuaciones", id, payload),
  deleteActuacion: (id) => deleteElement("actuaciones", id),
  // Indicadores
  fetchIndicadoresByTenant: (tenantId) =>
    fetchElements<IndicadoresAU>(
      "indicadores",
      [...INDICADORES_FIELDS],
      undefined,
      {
        proyecto: {
          tenant: { _eq: tenantId },
        },
      }
    ),
  async createIndicador(payload) {
    const [created] = await saveElementsType<IndicadoresAU>("indicadores", [
      payload,
    ]);
    return created;
  },
  updateIndicador: (id, payload) => updateElement("indicadores", id, payload),
  deleteIndicador: (id) => deleteElement("indicadores", id),
  // Valores
  createValores: (payloads) =>
    payloads.length
      ? saveElementsType<ValoresAU>("valores_aue", payloads)
      : Promise.resolve([]),
  updateValor: (id, payload) => updateElement("valores_aue", id, payload),
  deleteValor: (id) => deleteElement("valores_aue", id),
  // Archivos
  createFileItems: (payloads) =>
    payloads.length
      ? (saveElements("file", payloads) as Promise<FileItem[]>)
      : Promise.resolve([]),
};
